/*
 * Copyright (c) CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

const DB_NAME = 'CyberKoalaCallsCacheDB';
const STORE_NAME = 'fetchCache';
const DB_VERSION = 1;
let cryptoKey: CryptoKey | null = null; // Simple key in memory (per session). You can derive from session data or user secret.

const cacheKey = (url: string) => `cache_${url}`;


/**
 * Works only with JSON requests
 * @beta
 * @param url
 * @param options
 * @param ttl
 */
export async function fetchWithPersistentCacheUnencrypted(
  url: string,
  options?: RequestInit,
  ttl = 60_000
) {
  const cached = localStorage.getItem(cacheKey(url));
  if (cached) {
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp < ttl) return data;
  }

  const response = await fetch(url, options);
  const data = await response.json();
  localStorage.setItem(
    cacheKey(url),
    JSON.stringify({ data, timestamp: Date.now() })
  );
  return data;
}

// --- DB ---
async function getCryptoKey(): Promise<CryptoKey> {
  if (cryptoKey) return cryptoKey;

  const keyMaterial = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
  cryptoKey = keyMaterial;
  return keyMaterial;
}

async function encryptData(data: any): Promise<{ iv: Uint8Array; ciphertext: ArrayBuffer }> {
  const key = await getCryptoKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return { iv, ciphertext };
}

async function decryptData(iv: Uint8Array, ciphertext: ArrayBuffer): Promise<any> {
  const key = await getCryptoKey();
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return JSON.parse(new TextDecoder().decode(decrypted));
}

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getCachedResponse(url: string) {
  const db = await openDB();
  return new Promise<{ data: any; timestamp: number } | null>(
    (resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(url);

      request.onsuccess = async () => {
        const result = request.result;
        if (!result) return resolve(null);
        try {
          const data = await decryptData(new Uint8Array(result.iv), result.ciphertext);
          resolve({ data, timestamp: result.timestamp });
        } catch {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    }
  );
}

async function setCachedResponse(url: string, data: any) {
  const db = await openDB();
  const { iv, ciphertext } = await encryptData(data);

  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ url, iv: Array.from(iv), ciphertext, timestamp: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearFetchCache() {
  const db = await openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * @experimental
 * @param url
 * @param options
 * @param ttl in m
 */
export async function fetchWithPersistentCache(
  url: string,
  options?: RequestInit,
  ttl = 60_000
) {
  const cached = await getCachedResponse(url);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.data;
  }

  const response = await fetch(url, options);
  if (!response.ok) throw new Error(`Failed to fetch ${url}`);
  const data = await response.json();

  await setCachedResponse(url, data);
  return data;
}
